import Image from 'next/image';
import { useMemo } from 'react';

import { KeyValueRow, KeyValueTable } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { EventType, Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { ArgumentDisplay } from './components/ArgumentDisplay';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export const OnChainTriggerFullDetails = ({ trigger }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const getExecutedWhenLabel = useMemo(() => {
    const triggerEventOrFunction = trigger.signature.split('(')[0];
    return (
      <div className="tw:flex tw:flex-row tw:gap-1">
        <ArgumentDisplay
          value={triggerEventOrFunction}
          suffix={trigger.type === EventType.OnChainEvent ? 'event is emitted in' : 'function is called in'}
        />
      </div>
    );
  }, [trigger.signature, trigger.type]);

  const renderContracts = useMemo(() => {
    return trigger.contracts?.map((contract) => (
      <AddressChip key={contract.chainId} address={contract.address} chainId={contract.chainId} />
    ));
  }, [trigger.contracts]);

  const networkDisplay = useMemo(() => {
    // Get network info from the first contract
    const contract = trigger.contracts?.[0];
    if (!contract?.chainId || !contract?.network) return '-';

    const chainIconUrl = getChainIconUrl(contract.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={contract.network} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{contract.network.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [trigger.contracts, getChainIconUrl]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!trigger.currencyVolumeExpression) return '-';

    const contract = trigger.contracts?.find(
      (c) => c.address.toLowerCase() === trigger.currencyVolumeExpression?.toLowerCase(),
    );
    const chainId = contract?.chainId ?? trigger.contracts?.[0]?.chainId ?? 1;

    return <CurrencyDisplay value={trigger.currencyVolumeExpression} chainId={chainId} />;
  }, [trigger.currencyVolumeExpression, trigger.contracts]);

  const revenueCurrencyDisplay = useMemo(() => {
    if (!trigger.currencyRevenueExpression) return '-';

    const contract = trigger.contracts?.find(
      (c) => c.address.toLowerCase() === trigger.currencyRevenueExpression?.toLowerCase(),
    );
    const chainId = contract?.chainId ?? trigger.contracts?.[0]?.chainId ?? 1;

    return <CurrencyDisplay value={trigger.currencyRevenueExpression} chainId={chainId} />;
  }, [trigger.currencyRevenueExpression, trigger.contracts]);

  const userArgumentDisplay = useMemo(() => {
    if (!trigger.endUserIdentifierExpression) {
      return <ArgumentDisplay value="from" suffix="value of transaction" />;
    }
    return <ArgumentDisplay value={trigger.endUserIdentifierExpression} />;
  }, [trigger.endUserIdentifierExpression]);

  const volumeArgumentDisplay = useMemo(() => {
    if (!trigger.volumeExpression) return '-';
    return <ArgumentDisplay value={trigger.volumeExpression} />;
  }, [trigger.volumeExpression]);

  const revenueArgumentDisplay = useMemo(() => {
    if (!trigger.revenueExpression) return '-';
    return <ArgumentDisplay value={trigger.revenueExpression} />;
  }, [trigger.revenueExpression]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed when" value={getExecutedWhenLabel}>
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center tw:flex-wrap tw:ml-2">{renderContracts}</div>
      </KeyValueRow>
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="User argument" value={userArgumentDisplay} />
      <KeyValueRow label="Volume argument calculation" value={volumeArgumentDisplay} />
      <KeyValueRow label="Revenue argument calculation" value={revenueArgumentDisplay} />
      <KeyValueRow label="Revenue currency" value={revenueCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
};
